import React, { useState, useEffect } from 'react';
import BackButton from '../../components/common/UI/BackButton';
import { useDeposit } from '../../hooks/useDeposit';
import { API_BASE_URL } from '../../utils/constants';
import './Deposit.css';

const CryptoWalletDetails = ({ network, onWalletSelect, onBack }) => {
  const { getCryptoWallets } = useDeposit();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [exchangeRate, setExchangeRate] = useState(null);
  const [rateLoading, setRateLoading] = useState(true);

  useEffect(() => {
    const fetchWallets = async () => {
      try {
        // Pass network parameter correctly as object
        const data = await getCryptoWallets({ network: network.id });
        setWallets(data);
      } catch (error) {
        console.error('Error fetching wallets:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchWallets();
  }, [network.id]);

  // Fetch exchange rate to show minimum deposit amount
  useEffect(() => {
    const fetchExchangeRate = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/deposit/exchange-rate/`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        const data = await response.json();
        if (data.rate && !isNaN(data.rate)) {
          setExchangeRate(data.rate);
        } else {
          setExchangeRate(1500); // fallback
        }
      } catch (error) {
        console.error('Error fetching exchange rate:', error);
        setExchangeRate(1500); // fallback
      } finally {
        setRateLoading(false);
      }
    };

    fetchExchangeRate();
  }, []);

  const handleWalletSelect = (wallet) => {
    onWalletSelect(wallet);
  };

  const copyToClipboard = async (address, walletId) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedId(walletId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (loading) {
    return <div className="loading">Loading wallets...</div>;
  }

  if (wallets.length === 0) {
    return (
      <div className="crypto-wallet-details">
        <div className="section-header">
          <BackButton onClick={onBack} />
          <h2>Selected Network: {network.name}</h2>
        </div>
        <div className="no-wallets">
          <p>No active wallets found for {network.name} network.</p>
          <p>Please try another network or contact support.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="crypto-wallet-details">
      <div className="section-header">
        <BackButton onClick={onBack} />
        <h2>Selected Network: {network.name}</h2>
      </div>

      <div className="wallet-section">
        <h3 className="section-title">Wallet Address</h3>
        
        {wallets.map((wallet) => (
          <div key={wallet.id} className="wallet-card">
            <div className="wallet-address-container">
              <div className="wallet-address-label">Send only {network.currency} to this address</div>
              <div className="wallet-address-wrapper">
                <div className="wallet-address">{wallet.wallet_address}</div>
                <button 
                  className="copy-btn"
                  onClick={() => copyToClipboard(wallet.wallet_address, wallet.id)}
                  title="Copy address"
                >
                  {copiedId === wallet.id ? (
                    <span className="copy-icon">✓</span>
                  ) : (
                    <svg className="copy-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  )}
                </button>
              </div>
              {wallet.qr_code && (
                <div className="qr-code">
                  <img src={wallet.qr_code} alt="QR Code" />
                </div>
              )}
            </div>
            
            <button 
              className="select-wallet-btn"
              onClick={() => handleWalletSelect(wallet)}
            >
              Use This Wallet
            </button>
            <p className="wallet-minimum-amount">
              {rateLoading
                ? 'Loading minimum...'
                : `Minimum amount: $${(2000 / exchangeRate).toFixed(2)} ≈ ₦2,000 (Rate: $1 = ₦${exchangeRate.toLocaleString()})`
              }
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CryptoWalletDetails;